/**
 * Automations UI hooks — TanStack Query over the agents client resolved by `useAutomationsClient`
 * (the live agent-trust endpoints for an authed workspace; typed fixtures only when unauthenticated).
 * The client `scope` (workspace id, or "fixtures") is folded into every key so the two sources
 * never collide and a workspace switch refetches. The component API is unchanged.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  useIsMutating,
} from "@tanstack/react-query";
import type { AutomationPreferences } from "@xavior/schema";
import { useAutomationsClient } from "./useAutomationsClient";
import type { Automation, Autonomy } from "./fixtures";
import type { SimWindow } from "./fixtures-deep";

const ROOT = "agents-ui";
const key = (scope: string, ...rest: unknown[]) =>
  [ROOT, scope, ...rest] as const;

// Live polling cadence, gated by client scope. The unauthenticated fixture path NEVER polls: the
// fixture client re-derives fresh arrays on every call (and `delay(110ms)`s), so a recurring
// interval would churn-re-render the always-mounted rail badge on an idle screen for nothing.
// On the live path, in-pane run/list feeds poll fast so create→watch→finish needs no manual
// refresh (LIVE_FAST_MS); the notifications query polls gently (LIVE_SLOW_MS) because it is mounted
// app-wide for the SidebarRail badge — every authed session, pane open or not — and a pending
// sign-off badge does not need sub-second latency.
const LIVE_FAST_MS = 2500;
const LIVE_SLOW_MS = 30_000;
const livePoll = (scope: string, ms: number): number | false =>
  scope === "fixtures" ? false : ms;

export function useAutomationsList() {
  const { client, scope } = useAutomationsClient();
  // Pause the recurring roster refetch while ANY automations mutation is in flight, so a background
  // poll can't resolve mid-mutation and clobber an optimistic cache write — specifically the
  // capability-allowlist full-replace in useSetAutomationCapabilities (the #5340 wipe). onMutate's
  // cancelQueries handles the already-in-flight fetch; this stops a NEW interval fetch from
  // reopening that window while the PATCH is pending.
  const mutating = useIsMutating() > 0;
  return useQuery({
    queryKey: key(scope, "list"),
    queryFn: () => client.listAutomations(),
    refetchInterval: mutating ? false : livePoll(scope, LIVE_FAST_MS),
  });
}

/** In-flight runs across all agents — the "Running now" strip (live run-status feed). */
export function useRunningNow() {
  const { client, scope } = useAutomationsClient();
  return useQuery({
    queryKey: key(scope, "running-now"),
    queryFn: () => client.listRunningNow(),
    refetchInterval: livePoll(scope, LIVE_FAST_MS),
  });
}

export function useTemplates() {
  const { client, scope } = useAutomationsClient();
  return useQuery({
    queryKey: key(scope, "templates"),
    queryFn: () => client.listTemplates(),
  });
}

export function useSetAutomationEnabled() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      client.setEnabled(v.id, v.enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

export function useCreateAutomation() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (purpose: string) => client.createAutomation(purpose),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

export function useDeleteAutomation() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.deleteAutomation(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
      void qc.invalidateQueries({ queryKey: key(scope, "approvals") });
      void qc.invalidateQueries({ queryKey: key(scope, "activity") });
    },
  });
}

export function useSetAutomationColor() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; color: string }) =>
      client.setColor(v.id, v.color),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

export function useSetAutomationAutonomy() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; autonomy: Autonomy }) =>
      client.setAutonomy(v.id, v.autonomy),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

/** Save one automation's per-kind preferences (Phase 2). Mirrors useSetAutomationEnabled. */
export function useSetAutomationPreferences() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; preferences: AutomationPreferences }) =>
      client.setPreferences(v.id, v.preferences),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

/** Save one automation's `actionAllowlist` — the capability opt-in toggles (default off). The
 *  server validates the list against the known ACTION_VOCABULARY (D7). Mirrors the other setters,
 *  but writes the cache OPTIMISTICALLY.
 *
 *  Why optimistic (not a plain invalidate-on-success): the capability toggles compute the NEXT
 *  allowlist from the *cached* automation row and full-replace it. A plain `invalidateQueries`
 *  fires-and-forgets — `isPending` drops before the list refetch lands — so a second toggle flipped
 *  in that window would recompute from the PRE-first-toggle allowlist and full-replace it back,
 *  silently wiping the first capability server-side (#5340). Patching the cached row synchronously
 *  in `onMutate` (after cancelling any in-flight list refetch) means the second toggle always reads
 *  the first toggle's result. We roll back on error and re-sync from the server on settle. */
export function useSetAutomationCapabilities() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  const listKey = key(scope, "list");
  return useMutation({
    mutationFn: (v: { id: string; actionAllowlist: string[] }) =>
      client.setActionAllowlist(v.id, v.actionAllowlist),
    onMutate: async (v) => {
      // Cancel outgoing refetches so an in-flight (stale) list response can't clobber our
      // optimistic write after it lands.
      await qc.cancelQueries({ queryKey: listKey });
      const previous = qc.getQueryData<Automation[]>(listKey);
      qc.setQueryData<Automation[] | undefined>(listKey, (rows) =>
        rows?.map((row) =>
          row.id === v.id ? { ...row, actionAllowlist: v.actionAllowlist } : row,
        ),
      );
      return { previous };
    },
    onError: (_err, _v, ctx) => {
      // Restore the pre-toggle cache so a failed PATCH doesn't leave the toggle reading ON/OFF wrong.
      if (ctx?.previous) qc.setQueryData(listKey, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: listKey });
    },
  });
}

export function useRuns(agentId: string) {
  const { client, scope } = useAutomationsClient();
  return useQuery({
    queryKey: key(scope, "runs", agentId),
    queryFn: () => client.listRuns(agentId),
    refetchInterval: livePoll(scope, LIVE_FAST_MS),
  });
}

/** Simulate is on-demand (a button), so it's a mutation that returns the result. */
export function useSimulate(agentId: string) {
  const { client } = useAutomationsClient();
  return useMutation({
    mutationFn: (window: SimWindow) => client.simulate(agentId, window),
  });
}

export function useApprovals() {
  const { client, scope } = useAutomationsClient();
  return useQuery({
    queryKey: key(scope, "approvals"),
    queryFn: () => client.listApprovals(),
    refetchInterval: livePoll(scope, LIVE_FAST_MS),
  });
}

export function useDecideApproval() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      id: string;
      decision: "approved" | "edited" | "rejected";
      preparedPayload?: Record<string, unknown>;
    }) => client.decideApproval(v.id, v.decision, v.preparedPayload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "approvals") });
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
      // Refresh runs so an inline sign-off card in a run timeline clears once decided.
      void qc.invalidateQueries({ queryKey: key(scope, "runs") });
      // Refresh notifications so a resolved sign_off signal drops from the feed/count.
      void qc.invalidateQueries({ queryKey: key(scope, "notifications") });
    },
  });
}

export function useActivity() {
  const { client, scope } = useAutomationsClient();
  return useQuery({
    queryKey: key(scope, "activity"),
    queryFn: () => client.listActivity(),
    refetchInterval: livePoll(scope, LIVE_FAST_MS),
  });
}

/** The three typed notification signals (design §4); the feed sorts them by priority. */
export function useNotifications() {
  const { client, scope } = useAutomationsClient();
  return useQuery({
    queryKey: key(scope, "notifications"),
    queryFn: () => client.listNotifications(),
    refetchInterval: livePoll(scope, LIVE_SLOW_MS),
  });
}

export function useApplyRefinement() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { agentId: string; condition: string }) =>
      client.applyChatRefinement(v.agentId, v.condition),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

export function useUndoRefinement() {
  const { client, scope } = useAutomationsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => client.undoRefinement(agentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}
